import { useEffect, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import type {
  ConnectionDeleteResult,
  ConnectionListResult,
  ConnectionRepository,
  ConnectionVerifyResult,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQuery } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import {
  GoogleAnalyticsIntegrations,
  PlatformUrns,
} from '@mint-sdk/api-integrations';

const GA4_URN = PlatformUrns.TRAFFICKING_PLATFORM_URN.googleAnalytics;

export interface GA4ConnectionExtra {
  accountId: string | null;
  propertyId: number | null;
}

/**
 * Maps a raw GA4 integration item to the registry's `NormalizedConnection`
 * shape. Preserves two hardcodes from `packages/connections`'
 * `useGetConnectionList` GA4 branch (`// TODO: remove hack when BE is
 * ready`): `usingInAccounts` is always `false` (GA4 is never attached to ad
 * accounts), and `isExpired` is derived from `!isRefreshTokenValid` (with
 * `ga4BackendOwnedAuth`, from `isGA4IntegrationVerified` instead).
 */
export function toGA4NormalizedConnection(
  item: GoogleAnalyticsIntegrations.GoogleAnalyticsIntegrationType,
  isBackendOwnedAuth = false,
): NormalizedConnection {
  return {
    id: item.id,
    name: item.name,
    platformUrn: GA4_URN,
    // GA4's list endpoint carries no dedicated "verified" flag — `useVerify`
    // is the source of truth for that, so the list mapping leaves it unset.
    isVerified: null,
    isExpired: !GoogleAnalyticsIntegrations.isGA4IntegrationVerified(item, {
      isBackendOwnedAuth,
    }),
    usingInAccounts: false,
    userId: item.userUpdatedId ?? null,
    extra: {
      accountId: item.accountId,
      propertyId: item.propertyId,
    } satisfies GA4ConnectionExtra,
  };
}

function useGA4List(_params: { platform: string }): ConnectionListResult {
  const ff = useFlags('@mint/arm', ['ga4BackendOwnedAuth']);
  const isBackendOwnedAuth = ff.ga4BackendOwnedAuth.enabled;
  const { data, isLoading, refetch } = useQuery({
    ...GoogleAnalyticsIntegrations.getGA4Integrations({}),
  });

  const connections = useMemo(
    () =>
      (data ?? []).map((item) =>
        toGA4NormalizedConnection(item, isBackendOwnedAuth),
      ),
    [data, isBackendOwnedAuth],
  );

  return { connections, isLoading, refetch };
}

/**
 * GA4's "verify" is not its own endpoint — the connection's refresh-token
 * validity only comes back from the list response, so this re-fetches the
 * whole GA4 list and finds the item by id.
 *
 * // TODO: remove hack when BE is ready
 *
 * Ported as-is from `packages/connections/src/api/queries/useVerifyConnection.ts`'s
 * GA4 branch (same comment marks it there) — the point of this refactor is
 * to move the shim here, not to fix the underlying BE gap.
 */
function useGA4Verify({
  connectionId,
}: {
  platform: string;
  connectionId: string;
}): ConnectionVerifyResult {
  const { t } = useTranslation('@mint-lib/ga4-integration');
  const ff = useFlags('@mint/arm', ['ga4BackendOwnedAuth']);
  const isBackendOwnedAuth = ff.ga4BackendOwnedAuth.enabled;
  const isEnabled = !!connectionId;

  const { data, isLoading, error, refetch } = useQuery({
    ...GoogleAnalyticsIntegrations.getGA4Integrations({}),
    select: (items) => {
      const item = items?.find(({ id }) => id === connectionId);
      return item
        ? GoogleAnalyticsIntegrations.isGA4IntegrationVerified(item, {
            isBackendOwnedAuth,
          })
        : undefined;
    },
    enabled: isEnabled,
  });

  useEffect(() => {
    if (!error) return;
    enqueueSnackbar({
      title: t('Something went wrong'),
      subTitle: t('Please try again or contact the support.'),
      variant: 'error',
    });
  }, [error, t]);

  return {
    isVerified: data,
    isLoading,
    // The list endpoint this shim reuses never surfaces a WrongCustomerError
    // the way the generic ad-server verify endpoint does.
    isWrongCustomerError: false,
    refetch,
  };
}

function useGA4Delete(): ConnectionDeleteResult {
  const { mutate, isPending } =
    GoogleAnalyticsIntegrations.deleteGA4IntegrationMutation();

  return {
    remove: (id, { onSuccess, onError }) => {
      mutate({ urlParams: { integrationId: id } }, { onSuccess, onError });
    },
    isPending,
  };
}

export function createGA4Repository(): ConnectionRepository {
  return {
    useList: useGA4List,
    useVerify: useGA4Verify,
    useDelete: useGA4Delete,
  };
}
